import React, { useState, useMemo } from 'react';
import { Autocomplete, TextField, Chip, Box, FormControl, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

interface AutocompleteMultiSelectProps {
    options: string[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    size?: 'small' | 'medium';
    className?: string;
    sx?: any;
    filterOptions?: (options: string[], inputValue: string) => string[];
}

const AutocompleteMultiSelect: React.FC<AutocompleteMultiSelectProps> = ({
    options,
    value = [],
    onChange,
    placeholder = 'Select options...',
    label,
    disabled = false,
    size = 'small',
    className,
    sx = {},
    filterOptions,
}) => {
    const { themeStyle } = useThemeContext();
    const [inputValue, setInputValue] = useState('');

    // Filter options based on input value
    const filteredOptions = useMemo(() => {
        if (filterOptions) {
            return filterOptions(options, inputValue);
        }
        return options.filter(option => option.toLowerCase().includes(inputValue.toLowerCase()));
    }, [options, inputValue, filterOptions]);

    const handleChange = (event: any, newValue: string[]) => {
        onChange(newValue);
    };

    const handleInputChange = (event: any, newInputValue: string) => {
        setInputValue(newInputValue);
    };

    return (
        <FormControl fullWidth>
            {label && (
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        mb: 1,
                        fontSize: 14,
                    }}>
                    {label}
                </Typography>
            )}
            <Autocomplete
                multiple
                options={filteredOptions}
                value={value}
                onChange={handleChange}
                onInputChange={handleInputChange}
                inputValue={inputValue}
                disabled={disabled}
                size={size}
                className={className}
                sx={{
                    '& .MuiAutocomplete-inputRoot': {
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                        bgcolor: theme[themeStyle].secondaryBg,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].tag_white,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].tag_white,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].tag_white,
                        },
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                        color: theme[themeStyle].tag_white,
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                        color: theme[themeStyle].tag_white,
                    },
                    ...sx,
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            size="small"
                            sx={{
                                bgcolor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                '& .MuiChip-deleteIcon': {
                                    color: theme[themeStyle].black,
                                },
                            }}
                        />
                    ))
                }
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.7,
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <Box
                        component="li"
                        {...props}
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                            bgcolor: theme[themeStyle].secondaryBg,
                            '&:hover': {
                                bgcolor: theme[themeStyle].highlight + '33',
                            },
                            '&.Mui-focused': {
                                bgcolor: theme[themeStyle].highlight + '33',
                            },
                        }}>
                        {option}
                    </Box>
                )}
                ListboxProps={{
                    sx: {
                        bgcolor: theme[themeStyle].secondaryBg,
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        '& .MuiAutocomplete-option': {
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                bgcolor: theme[themeStyle].highlight + '33',
                            },
                            '&.Mui-focused': {
                                bgcolor: theme[themeStyle].highlight + '33',
                            },
                        },
                    },
                }}
                PaperComponent={({ children, ...other }) => (
                    <Box
                        {...other}
                        sx={{
                            bgcolor: theme[themeStyle].secondaryBg,
                            color: theme[themeStyle].tag_white,
                            border: `1px solid ${theme[themeStyle].grey}`,
                            borderRadius: 1,
                        }}>
                        {children}
                    </Box>
                )}
            />
        </FormControl>
    );
};

export default AutocompleteMultiSelect;
